<template>
  <div>
    <Breadcrumbs
      :breadcrumbs="breadcrumbs">

      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <va-popover
          placement="left"
          :message="$t('statistics.csm.download')">
          <span class="i-cloud-download" @click.prevent="exportCsm"></span>
        </va-popover>
      </div>

    </Breadcrumbs>

    <section class="filters row evollu-form">

      <div class="col-12 col-md-4">
        <range-date-picker
          id="statistics-date-range"
          :label="$t('clinical.waiting_room_statistics.date_range')"
          v-model="filters.dateRange"
          placeholder="---"
          :config="{ maxInterval: { value: 6, unit: 'months' } }"
          :clearable="false"
        >
        </range-date-picker>
      </div>

      <div class="col-12 col-md-4">
        <label class="form-label">
          {{ $t('statistics.csm.title') }}
        </label>
        <multiselect
          id="csm-filter"
          v-model="filters.csmUsers"
          :options="csmUsersOptions"
          track-by="id"
          :customLabel="formatedUserLabel"
          :placeholder="csmUsersOptions.length ? $t('statistics.csm.all_users') : $t('statistics.csm.no_users')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!csmUsersOptions.length || isRequestingData"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.csmUsers.length" @mousedown.prevent.stop="filters.csmUsers = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-4">
        <label class="form-label">
          {{ $t('statistics.csm.entities') }}
        </label>
        <multiselect
          id="entities-filter"
          v-model="filters.entities"
          :options="entityOptions"
          track-by="uid"
          :customLabel="formatedEntityLabel"
          :placeholder="entityOptions.length ? $t('clinical.waiting_room_statistics.several_entities') : $t('clinical.waiting_room_statistics.no_entities')"
          :allow-empty="true"
          :multiple="true"
          :limit="3"
          :disabled="!entityOptions.length || isRequestingData"
          :internal-search="false"
          @search-change="asyncFind"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.entities.length" @mousedown.prevent.stop="filters.entities = []"></span>
          </template>
        </multiselect>
      </div>
    </section>

    <fade-transition :duration="200" mode="out-in">
      <section v-if="isLoading || isRequestingData" class="text-center py-5">
        <span class="is-bold">{{$t('loading.message')}}</span>
      </section>

      <section v-else class="csm-statistics" key="csm-statistics">
        <span>{{$t('statistics.csm.showing_results_for_entities', {count: stats.entities.length})}} </span>
        <dashboard-info-widgets
          v-bind="propsDashboard"
          class="mt-1"
        />

        <!-- Graph statistics -->
        <ranking-graph
          v-for="graph in graphTypes"
          :key="graph.id"
          :graph="graph"
        ></ranking-graph>

      </section>
    </fade-transition>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/common/Breadcrumbs'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import Multiselect from 'vue-multiselect'
import { get as _get, uniqBy } from 'lodash'
import { StatisticsService } from '@/api/LeadsService'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import KPICard from '@/components/common/KPICard'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import { UserService } from '@/api/AuthService'
import DashboardInfoWidgets from '@/components/customer-success-manager/statistics/components/DashboardInfoWidgets'
import RankingGraph from '@/components/customer-success-manager/statistics/components/RankingGraph'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { FILTERS_CSM } from '@/local-storage/LocalStorageKeys'
import { mapGetters } from 'vuex'
import {AppointmentService} from "@/api/HomelabService";

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  mixins: [
    ClustersAndEntitiesMixin,
    MultiselectLabelFormatter
  ],
  components: {
    RankingGraph,
    DashboardInfoWidgets,
    KPICard,
    FadeTransition,
    RangeDatePicker,
    Breadcrumbs,
    Multiselect
  },
  data: function () {
    return {
      propsDashboard: {
        daysWithScreenings: 0,
        users: 0,
        appointments: 0,
        totalRecordsMade: 0,
        submitted: 0,
        lq: 0,
        avgLqDay: 0,
        avgLqEntity: 0
      },
      stats: {},
      entityOptions: [],
      csmUsersOptions: [],
      selectedEntityIds: [],
      selectedEntityUids: [],
      csmInfo: [],
      filters: {
        entities: [],
        csmUsers: [],
        dateRange: {
          start: this.moment().startOf('month').format('YYYY-MM-DD'),
          end: this.moment().endOf('month').format('YYYY-MM-DD')
        }
      },
      isLoading: true,
      isRequestingData: true,
      hasLoadedLocalStorage: false,
      pallete: ['#266F62', '#279381', '#28B199', '#29C4A9', '#68D5C2'],
    }
  },
  computed: {
    ...mapGetters({
      clusters: 'clusters'
    }),
    breadcrumbs: function () {
      return [
        { title: this.$t(`menu.${this.serviceSlug}`), disabled: true },
        { title: this.$t('menu.customer-success-manager/statistics'), active: true },
      ]
    },
    graphTypes: function () {
      return [
        {
          id: 1,
          name: this.$t('statistics.csm.graphs.per_entity.title'),
          labels: [
            { key: 'submitted', text: this.$t('statistics.csm.submitted'), color: this.pallete[0] },
            { key: 'lq', text: this.$t('statistics.csm.lq'), color: this.pallete[1] },
            { key: 'lqe', text: this.$t('statistics.csm.lqe'), color: this.pallete[2] },
            { key: 'appointments', text: this.$t('statistics.csm.appointments'), color: this.pallete[3] },
          ],
          dataset: this.stats.entities || [],
          dataset_key: 'entity_name',
          localStorageKey: 'STATS_TOP_ENTITIES',
        },
        {
          id: 2,
          name: this.$t('statistics.csm.graphs.per_entity_user.title'),
          labels: [
            { key: 'submitted', text: this.$t('statistics.csm.submitted'), color: this.pallete[0] },
            { key: 'lq', text: this.$t('statistics.csm.lq'), color: this.pallete[1] },
            { key: 'lqe', text: this.$t('statistics.csm.lqe'), color: this.pallete[2] },
          ],
          dataset: this.stats.entityUsers || [],
          dataset_key: 'name',
          localStorageKey: 'STATS_TOP_ENTITY_USERS',
        }
      ]
    },
    authorizedEntities: function () {
      let audiologyService = this.$store.state.services.find(service => service.url === this.$store.state.evollead_server) || null
      if (audiologyService) {
        if (audiologyService.user_permissions.some(p => p.entity_id === null)) {
          return audiologyService.entities
        } else {
          return audiologyService.entities.filter(entity => audiologyService.user_permissions.filter(p => p.entity_id === entity.id))
        }
      }
      return null
    },
  },
  created: async function () {
    await this.getCsmInfo()
      .then(async () => {
        // const entities = uniqBy(this.csmInfo.map(datasetEntry => datasetEntry.entity), 'id')
        // this.entityOptions = this.createClustersAndEntitiesList(this.clusters, entities)
        this.csmUsersOptions = uniqBy(this.csmInfo.map(item => item.user), 'id')
        this.entityOptions = this.createClustersAndEntitiesList(this.clusters, this.authorizedEntities)
        await this.loadLocalStorage()
        await this.getStatistics()
          .then(() => {
            this.isLoading = false
            this.isRequestingData = false
          })
      })
  },
  methods: {
    getCsmInfo: function () {
      return new Promise((resolve, reject) => {
        UserService.csmInfo()
          .then(data => {
            this.csmInfo = data.entitiesWithCsm
            resolve()
          })
          .catch(err => {
            this.showToast(this.$t('clinical.waiting_room_statistics.something_went_wrong'), this.$store.state.errorToastOptions)
            reject(err)
          })
      })
    },
    getStatistics: function () {
      return new Promise((resolve, reject) => {
        StatisticsService.getCsmStats(this.getActiveFilters())
          .then(data => {
            // console.log(data)
            this.stats = data[0]

            this.propsDashboard.daysWithScreenings = data[0].nr_days_with_screenings
            this.propsDashboard.users = data[0].users
            this.propsDashboard.appointments = data[0].appointments
            this.propsDashboard.totalRecordsMade = data[0].total_records
            this.propsDashboard.submitted = data[0].submitted
            this.propsDashboard.lq = data[0].lq
            this.propsDashboard.avgLqDay = data[0].avg_lq_day
            this.propsDashboard.avgLqEntity = data[0].avg_lq_entity

            this.saveLocalStorage()

            resolve()
          })
          .catch(errors => {
            console.log(errors)
            this.showToast(this.$t('clinical.waiting_room_statistics.something_went_wrong'), this.$store.state.errorToastOptions)
            reject(errors)
          })
      })
    },
    getActiveFilters: function () {
      let entities = null
      if (this.filters.entities.length) {
        if(this.filters.entities[0].entities !== undefined) {
            entities = this.filters.entities[0].entities.map(entity => entity.id).join(',')
        } else {
          entities = this.filters.entities.map(entity => entity.id).join(',')
        }
      }

      let filters = {
        start: _get(this.filters, 'dateRange.start', null) || undefined,
        end: _get(this.filters, 'dateRange.end', null) || undefined,
        ...this.filters.csmUsers.length && { csmUsers: this.filters.csmUsers.map(user => user.id).join(',') },
        selectedEntityUids: Array.from(this.selectedEntityUids) || null
      }
      if(entities) {
        filters.entities = entities
      }
      return filters
    },
    loadLocalStorage: async function () {
      const state = LocalStorageHelper.get(FILTERS_CSM)
      if (state) {
        // s date filters from local storage
        if (state.start && state.start !== '' && state.end && state.end !== '') {
          this.filters.dateRange.start = state.start
          this.filters.dateRange.end = state.end
        }

        /* if (state.entities && state.entities !== '') {
          let entitiesIds = state.entities.split(',')
          this.filters.entities = this.entityOptions.filter(entity => entitiesIds.some(id => id === entity.id.toString()))
        } */

        if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids && state.selectedEntityUids.length > 0) {
          this.filters.entities = this.reloadSelectedClustersAndEntities(this.entityOptions, state.selectedEntityUids)
        }

        if (state.csmUsers && state.csmUsers !== '') {
          let csmIds = state.csmUsers.split(',')
          this.filters.csmUsers = this.csmUsersOptions.filter(user => csmIds.some(id => id === user.id.toString()))
        }
      }
    },
    saveLocalStorage: function () {
      LocalStorageHelper.set(FILTERS_CSM, {
        ...this.getActiveFilters(),
      })
      this.hasLoadedLocalStorage = true
    },
    asyncFind(search_query){
      let csmUsersIds = this.filters.csmUsers.map(user => user.id)
      if (csmUsersIds.length && csmUsersIds.length > 0) {
        const entities = uniqBy(this.csmInfo.filter(item => csmUsersIds.some(id => id === item.responsible_user_id)).map(datasetEntry => datasetEntry.entity), 'id')
        this.entityOptions = this.createClustersAndEntitiesListSearch(this.clusters, entities , search_query)
      } else {
        const entities = uniqBy(this.csmInfo.map(datasetEntry => datasetEntry.entity), 'id')
        this.entityOptions = this.createClustersAndEntitiesListSearch(this.clusters, entities , search_query)
      }
    },
    exportCsm: function(){
      let activeFilters = this.getActiveFilters()
      StatisticsService.exportCsm(activeFilters)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'csm.xlsx')
          document.body.appendChild(link)
          link.click()
        })
        .catch(errors => this.showToast(this.$t('homelab.something_went_wrong'), this.$store.state.errorToastOptions))

    }
  },
  watch: {
    'filters.dateRange': {
      handler: function () {
        if (this.hasLoadedLocalStorage) {
          this.isRequestingData = true
          this.getStatistics()
            .then(() => {
              this.isRequestingData = false
            })
        }
      },
      deep: true
    },
    'filters.csmUsers': function (value) {
      if (this.hasLoadedLocalStorage) {
        this.isRequestingData = true
        let csmUsersIds = value.map(user => user.id)
        if (csmUsersIds.length && csmUsersIds.length > 0) {
          const entities = uniqBy(this.csmInfo.filter(item => csmUsersIds.some(id => id === item.responsible_user_id)).map(datasetEntry => datasetEntry.entity), 'id')
          this.entityOptions = this.createClustersAndEntitiesList(this.clusters, entities)
        } else {
          const entities = uniqBy(this.csmInfo.map(datasetEntry => datasetEntry.entity), 'id')
          this.entityOptions = this.createClustersAndEntitiesList(this.clusters, entities)
        }

        if (this.entityOptions.length && this.entityOptions.length === 1) {
          this.filters.entities = this.entityOptions[0]
        }

        this.getStatistics()
          .then(() => {
            this.isRequestingData = false
          })
      }
    },
    'filters.entities': function (options) {
      if (this.hasLoadedLocalStorage) {
        const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
        this.selectedEntityIds = Array.from(selectedOptions.ids).join(',')
        this.selectedEntityUids = selectedOptions.uids
        this.isRequestingData = true
        if (this.selectedEntityIds.length && this.selectedEntityIds.length > 0) {
          this.csmUsersOptions = uniqBy(this.csmInfo.filter(item => this.selectedEntityIds.includes(item.entity_id)).map(item => item.user), 'id')
        } else {
          this.csmUsersOptions = uniqBy(this.csmInfo.map(item => item.user), 'id')
        }

        if (this.csmUsersOptions.length && this.csmUsersOptions.length === 1) {
          this.filters.csmUsers = this.csmUsersOptions[0]
        }

        this.getStatistics()
          .then(() => {
            this.isRequestingData = false
          })
      }
    }
  }
}
</script>

<style lang="scss">
.centered-icon-text {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.icon-custom-size{
  width: 30px;
  height: 35px
}

.kpi-card {
  max-width: 20rem;
}

</style>
